import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LockIcon, Minus, Plus, ShieldCheck } from 'lucide-react';
import { StoreIcon, HeartIcon, ShoppingBagIcon } from './icons.jsx';
import { useAppStore } from "../store/Store"
import CartIcon from '../icons/CartIcon.jsx';

const NavItem = ({ pageName, currentPage, onNavigate, children, label }) => {
  const isActive = currentPage === pageName;
  return (
    <a
      href="#"
      onClick={(e) => { e.preventDefault(); onNavigate(pageName); }}
      className="flex flex-col items-center justify-center flex-1 h-full group"
      aria-current={isActive ? 'page' : undefined}
    >
      <div
        className={`flex flex-col items-center justify-center w-14 h-14 rounded-xl transition-all duration-300 ease-in-out ${isActive ? 'bg-gray-100' : 'bg-transparent'}`}
      >
        {children}
        <span className={`text-xs mt-1 transition-colors duration-200 ${isActive ? 'text-gray-800 font-semibold' : 'text-gray-500'}`}>{label}</span>
        <div
          className={`h-[3px] w-6 bg-gray-400 rounded-full transition-all duration-300 transform mt-1 ${isActive ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}
        ></div>
      </div>
    </a>
  );
};

// Navigation 1: Multi-page navigation
const MultiPageNav = ({ currentPage, onNavigate }) => {
  const { cartCount, wishlistCount } = useAppStore();

  const iconBaseClass = "h-5 w-5 transition-colors duration-300";
  const inactiveClass = "text-gray-500 group-hover:text-gray-800";
  const activeClass = "text-gray-800";


  return (
    <nav className="flex justify-around items-center h-[60px] px-2" style={{ paddingTop: '5px' }}>
      <NavItem pageName="shop" currentPage={currentPage} onNavigate={onNavigate} label="Shop">
        <StoreIcon className={`${iconBaseClass} ${currentPage === 'shop' ? activeClass : inactiveClass}`} />
      </NavItem>

      <NavItem pageName="cart" currentPage={currentPage} onNavigate={onNavigate} label="Cart">
        <div className="relative">
          <ShoppingBagIcon className={`${iconBaseClass} ${currentPage === 'cart' ? activeClass : inactiveClass}`} />
          {cartCount > 0 &&
            (<span className="absolute top-[-6px] right-[-9px] block h-4 w-4 rounded-full bg-black text-white text-xs flex items-center justify-center">
              {cartCount}
            </span>)}
        </div>
      </NavItem>

      <NavItem pageName="wishlist" currentPage={currentPage} onNavigate={onNavigate} label="Wishlist">
        <div className="relative">
          <HeartIcon className={`${iconBaseClass} ${currentPage === 'wishlist' ? activeClass : inactiveClass}`} />
          {wishlistCount > 0 &&
            (<span className="absolute top-[-6px] right-[-9px] block h-4 w-4 rounded-full bg-black text-white text-xs flex items-center justify-center">
              {wishlistCount}
            </span>)}
        </div>
      </NavItem>
    </nav>
  );
};

// Navigation 2: Checkout with subtotal (for cart page)
const CheckoutSubtotalNav = () => {
  const { cartCount, cartItems, getCartTotal } = useAppStore();
  const isCheckingOut = false;

  // Calculate actual subtotal from cart items
  const subtotal = getCartTotal();

  return (
    <div className="flex-shrink-0 bg-white border-t border-gray-100 px-4 py-3 z-20">
      {/* Subtotal Row */}
      <div className="flex justify-between items-center mb-2">
        <span className="text-[12px] text-gray-600">
          Subtotal • <span className="text-gray-400">{cartCount} items</span>
        </span>
        <span className="text-[18px] font-semibold text-gray-900">
          ৳{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>

      {/* Checkout Button with inline security */}
      <button
        disabled={isCheckingOut || cartCount === 0}
        className="
          w-full bg-gray-900 hover:bg-black 
          text-white font-medium text-[13px]
          py-2.5 rounded-xl
          flex flex-col items-center justify-center
          transition-all duration-200 active:scale-[0.97]
          disabled:opacity-70 disabled:cursor-not-allowed shadow-sm
        "
      >
        <div className="flex items-center gap-1.5">
          {isCheckingOut ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Processing</span>
            </>
          ) : (
            <>
              <LockIcon size={14} />
              <span>Checkout</span>
            </>
          )}
        </div>

        {/* Inline security line */}
        {!isCheckingOut && (
          <span className="flex items-center gap-1 mt-1 text-[10px] text-gray-300">
            <ShieldCheck size={10} className="text-green-400" />
            Secure Payment
          </span>
        )}
      </button>
    </div>
  );
};

// Navigation 3: Checkout + quantity + cart (for product pages)
const CheckoutQuantityCartNav = () => {
  const [quantity, setQuantity] = useState(1);
  const { cartCount } = useAppStore();

  const increment = () => setQuantity(q => Math.min(10, q + 1));
  const decrement = () => setQuantity(q => Math.max(1, q - 1));

  return (
    <nav className="flex justify-around items-center w-full h-[60px]">
      <div className="flex items-center gap-3 w-full h-10 px-5">
        {/* Quantity Selector */}
        <div className="flex items-center bg-gray-100 rounded-lg p-0.5 h-full w-[100px] flex-shrink-0">
          <button
            onClick={decrement}
            disabled={quantity <= 1}
            className="w-8 h-full flex items-center justify-center text-gray-500 hover:bg-white hover:shadow-sm rounded-md transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:shadow-none"
          >
            <Minus size={14} strokeWidth={2.5} />
          </button>
          <span className="flex-1 text-center font-bold text-gray-900 text-sm select-none">{quantity}</span>
          <button
            onClick={increment}
            className="w-8 h-full flex items-center justify-center text-gray-500 hover:bg-white hover:shadow-sm rounded-md transition-all"
          >
            <Plus size={14} strokeWidth={2.5} />
          </button>
        </div>

        {/* Checkout Button */}
        <button className="flex-1 h-full bg-gray-900 text-white rounded-lg flex items-center justify-center gap-2 shadow-lg shadow-gray-200 active:scale-[0.98] transition-all hover:bg-black">
          <span className="text-sm font-bold tracking-wide">Order Now</span>
        </button>

        {/* Cart Button */}
        <button className="w-[40px] h-full bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-all hover:bg-black relative">
          <span className="text-sm font-bold tracking-wide"><CartIcon /></span>
        </button>
      </div>
    </nav>
  );
};


// Navigation 4: Checkout page navigation (for checkout page)
const CheckoutPageNav = () => {
  return (
    <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 px-4 py-2 z-50 flex flex-col items-center justify-center max-h-[85px] shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">

      {/* Place Order Button */}
      <button
        className="w-full bg-black text-white font-bold py-3 rounded-xl text-sm shadow-md hover:shadow-lg active:scale-[0.97] transition-all duration-150"
      >
        Place Order
      </button>

      {/* Terms notice */}
      <p className="text-[10px] text-gray-400 text-center mt-1 leading-tight">
        By placing this order, you agree to our Terms of Service
      </p>
    </div>
  );
};



const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartCount } = useAppStore();

  // Determine current page based on route
  const getCurrentPage = () => {
    const path = location.pathname;

    if (path === '/shop' || path.startsWith('/shop/')) return 'shop';
    if (path === '/cart') return 'cart';
    if (path === '/wishlist') return 'wishlist';

    return 'shop'; // default fallback
  };

  const currentPage = getCurrentPage();

  // Navigation handler
  const handleNavigate = (pageName) => {
    switch (pageName) {
      case 'shop':
        navigate('/shop');
        break;
      case 'cart':
        navigate('/cart');
        break;
      case 'wishlist':
        navigate('/wishlist');
        break;
      default:
        navigate('/shop');
    }
  };

  // Determine which navigation to show
  const getNavigationComponent = () => {
    const isProductPage = location.pathname.startsWith("/shop/") && location.pathname.split("/").length === 4;
    const isCheckoutPage = location.pathname === '/checkout';

    if (location.pathname === '/cart' && cartCount === 0) {
      return <MultiPageNav currentPage={currentPage} onNavigate={handleNavigate} />

    }
    else if (isProductPage) {
      return <CheckoutQuantityCartNav />;
    } else {
      return <MultiPageNav currentPage={currentPage} onNavigate={handleNavigate} />;
    }
  };

  return (
    <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30 md:hidden">
      {getNavigationComponent()}
    </footer>
  );
};

export default BottomNav;